

import { useState } from "react";

interface QuotationItem {
  id?: number;
  service_id?: number;
  description?: string;
  vendor_id?: number;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  notes?: string;
}

interface Vendor {
  id: number;
  name: string;
}

interface QuotationServicesTableProps {
  services: QuotationItem[];
  vendors: Vendor[];
  onAddService: () => void;
  onEditService: (index: number) => void;
  onRemoveService: (index: number) => void;
  onUpdateService: (index: number, field: keyof QuotationItem, value: any) => void;
  onBulkAdd: () => void;
}

export default function QuotationServicesTable({
  services,
  vendors,
  onAddService,
  onEditService,
  onRemoveService,
  onUpdateService,
  onBulkAdd,
}: QuotationServicesTableProps) {
  const [removeConfirm, setRemoveConfirm] = useState<number | null>(null);

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">2</span>
          Event Services
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onBulkAdd}
            className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors whitespace-nowrap"
          >
            + Bulk Add
          </button>
          <button
            type="button"
            onClick={onAddService}
            className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors whitespace-nowrap"
          >
            + Add Service
          </button>
        </div>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-6 bg-stone-50 rounded-lg border border-dashed border-stone-300">
          <p className="text-sm text-stone-400">No services added yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-3 py-2 sm:py-2.5 font-semibold text-stone-700 w-[40%]">Service</th>
                <th className="text-center px-2 py-2 sm:py-2.5 font-semibold text-stone-700 w-[15%]">Qty</th>
                <th className="text-right px-2 py-2 sm:py-2.5 font-semibold text-stone-700 w-[15%]">Price</th>
                <th className="text-right px-2 py-2 sm:py-2.5 font-semibold text-stone-700 w-[15%]">Total</th>
                <th className="text-center px-2 py-2 sm:py-2.5 font-semibold text-stone-700 w-[15%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((item, index) => (
                <tr key={index} className="border-b border-stone-100 hover:bg-amber-50/30 transition-colors">
                  <td className="px-3 py-2 sm:py-2.5">
                    <div>
                      <p className="font-medium text-stone-700 truncate">{item.description}</p>
                      {item.vendor_id && (
                        <p className="text-xs text-stone-500 truncate">
                          {vendors.find(v => v.id === item.vendor_id)?.name || 'Unknown'}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 sm:py-2.5 text-center">
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => onUpdateService(index, "quantity", parseInt(e.target.value) || 1)}
                      className="w-full px-1.5 py-1 rounded border border-stone-200 text-center text-xs sm:text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                    />
                  </td>
                  <td className="px-2 py-2 sm:py-2.5 text-right">
                    <div className="relative">
                      <span className="absolute left-1 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-xs">₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => onUpdateService(index, "unit_price", parseFloat(e.target.value) || 0)}
                        className="w-full pl-4 pr-1.5 py-1 rounded border border-stone-200 text-right text-xs sm:text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-2 sm:py-2.5 text-right font-semibold text-stone-700 text-xs sm:text-sm">
                    ₹{(item.quantity * item.unit_price).toLocaleString('en-IN')}
                  </td>
                  <td className="px-2 py-2 sm:py-2.5 text-center">
                    <div className="flex gap-1 justify-center flex-wrap">
                      <button
                        type="button"
                        onClick={() => onEditService(index)}
                        className="px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemoveConfirm(index)}
                        className="px-1.5 py-0.5 text-xs rounded bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removeConfirm !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-bold text-stone-800 mb-2">Remove Service?</h3>
            <p className="text-sm text-stone-600 mb-6">
              Are you sure you want to remove <strong>{services[removeConfirm].description}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setRemoveConfirm(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-600 font-semibold text-sm hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onRemoveService(removeConfirm);
                  setRemoveConfirm(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold text-sm hover:bg-rose-700 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
